import { STATIONS, formatDuration, type Segment, type SegmentType } from "@roxpacer/core";
import { useState } from "react";
import { useEstimate } from "../lib/use-estimate";
import { useActivePlan } from "../store";

const SERIES: { type: SegmentType; label: string; color: string }[] = [
  { type: "run", label: "Run", color: "bg-run" },
  { type: "roxzone", label: "Roxzone", color: "bg-roxzone" },
  { type: "station", label: "Station", color: "bg-station" },
  { type: "handover", label: "Handover", color: "bg-handover" },
];
const colorOf = (type: SegmentType) => SERIES.find((s) => s.type === type)!.color;

/**
 * Time per leg as stacked horizontal bars. Decorative for assistive tech: the split
 * table carries the same values.
 */
export function SplitChart() {
  const plan = useActivePlan();
  const estimate = useEstimate();
  const [hovered, setHovered] = useState<Segment | null>(null);

  const legs = STATIONS.map((station, i) => {
    const segments = estimate.segments.filter((s) => s.leg === i + 1);
    return { station, segments, total: segments.reduce((sum, s) => sum + s.targetSec, 0) };
  });
  const longest = Math.max(...legs.map((l) => l.total), 1);
  const series = SERIES.filter((s) => s.type !== "handover" || plan.format === "relay");

  return (
    <section className="card p-4" aria-labelledby="chart-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="chart-heading" className="label">
          Time per leg
        </h2>
        <ul className="flex flex-wrap gap-3 text-xs text-ink-2" aria-hidden>
          {series.map((s) => (
            <li key={s.type} className="flex items-center gap-1.5">
              <span className={`size-2.5 rounded-sm ${s.color}`} />
              {s.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-3 space-y-1.5" aria-hidden onPointerLeave={() => setHovered(null)}>
        {legs.map(({ station, segments, total }) => (
          <div key={station.id} className="grid grid-cols-[4.5rem_1fr_3rem] items-center gap-2">
            <span className="truncate text-xs text-ink-2">{station.shortName}</span>
            <div className="flex h-5 gap-[2px]" style={{ width: `${(total / longest) * 100}%` }}>
              {segments.map((segment) => (
                <div
                  key={segment.id}
                  className={`h-full min-w-[2px] last:rounded-r ${colorOf(segment.type)} ${
                    hovered && hovered.id !== segment.id ? "opacity-60" : ""
                  }`}
                  style={{ flexGrow: segment.targetSec, flexBasis: 0 }}
                  onPointerEnter={() => setHovered(segment)}
                />
              ))}
            </div>
            <span className="text-right text-xs tabular-nums text-ink-2">
              {formatDuration(total)}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 h-4 text-xs text-ink-2" aria-hidden>
        {hovered ? (
          <>
            <span className="font-semibold text-ink tabular-nums">
              {formatDuration(hovered.targetSec)}
            </span>{" "}
            {hovered.label} · ends at {formatDuration(hovered.endSec)}
          </>
        ) : (
          "Hover a bar for details"
        )}
      </p>
    </section>
  );
}
